import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Checkbox } from '../Checkbox';
import { Spinner } from '../Spinner';
import { cn } from '../cn';
import { useAuth } from '@saas-kit/lib/auth';

import { downloadCsv, rowsToCsv } from './exportCsv';
import { HeaderCell } from './HeaderCell';
import { Pagination } from './Pagination';
import { Toolbar } from './Toolbar';
import type { DataTableProps } from './types';

export function DataTable<T>({
  data,
  columns,
  rowKey,
  testId = 'datatable',
  search,
  onSearchChange,
  searchPlaceholder,
  filters,
  onFilterChange,
  sort,
  onSortChange,
  pagination,
  selectable = false,
  bulkActions,
  exportable = false,
  exportFilename,
  isLoading,
  error,
  emptyText,
}: DataTableProps<T>): JSX.Element {
  const { t } = useTranslation('common');
  const perms = useAuth((s) => s.user?.effective_permissions ?? []);
  const [selectedKeys, setSelectedKeys] = useState<Set<string>>(new Set());

  const keyedRows = useMemo(
    () => data.map((row) => ({ key: rowKey(row), row })),
    [data, rowKey],
  );
  const selectedRows = useMemo(
    () => keyedRows.filter((r) => selectedKeys.has(r.key)).map((r) => r.row),
    [keyedRows, selectedKeys],
  );
  const allSelected = keyedRows.length > 0 && selectedRows.length === keyedRows.length;

  const toggleAll = (): void => {
    setSelectedKeys((cur) =>
      cur.size === keyedRows.length ? new Set() : new Set(keyedRows.map((r) => r.key)),
    );
  };
  const toggleOne = (key: string): void => {
    setSelectedKeys((cur) => {
      const next = new Set(cur);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  return (
    <div
      className="overflow-hidden rounded-lg border border-border bg-card text-card-foreground shadow-sm"
      data-testid={testId}
    >
      <Toolbar<T>
        columns={columns}
        {...(search !== undefined ? { search } : {})}
        {...(onSearchChange !== undefined ? { onSearchChange } : {})}
        {...(searchPlaceholder !== undefined ? { searchPlaceholder } : {})}
        {...(filters !== undefined ? { filters } : {})}
        {...(onFilterChange !== undefined ? { onFilterChange } : {})}
        selectedRows={selectedRows}
        {...(bulkActions !== undefined ? { bulkActions } : {})}
        hasPermission={(p) => perms.includes(p)}
        exportable={exportable}
        onExport={() => {
          const sourceRows = selectedRows.length > 0 ? selectedRows : data;
          downloadCsv(exportFilename ?? testId, rowsToCsv(sourceRows, columns));
        }}
        onClearSelection={() => setSelectedKeys(new Set())}
      />
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-muted/50 text-start text-xs uppercase tracking-wide text-muted-foreground">
            <tr>
              {selectable ? (
                <th className="w-10 px-3 py-2">
                  <Checkbox
                    checked={allSelected}
                    onChange={toggleAll}
                    aria-label={t('dataTable.select_all')}
                    data-testid={`${testId}-select-all`}
                  />
                </th>
              ) : null}
              {columns.map((col) => (
                <HeaderCell
                  key={col.id}
                  column={col}
                  sort={sort ?? null}
                  onSortChange={onSortChange ?? null}
                />
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {isLoading ? (
              <tr>
                <td
                  className="px-4 py-10 text-center text-sm text-muted-foreground"
                  colSpan={columns.length + (selectable ? 1 : 0)}
                >
                  <Spinner size="sm" />
                </td>
              </tr>
            ) : error ? (
              <tr>
                <td
                  className="px-4 py-10 text-center text-sm text-destructive"
                  colSpan={columns.length + (selectable ? 1 : 0)}
                >
                  {error}
                </td>
              </tr>
            ) : keyedRows.length === 0 ? (
              <tr>
                <td
                  className="px-4 py-10 text-center text-sm text-muted-foreground"
                  colSpan={columns.length + (selectable ? 1 : 0)}
                >
                  {emptyText ?? t('dataTable.empty')}
                </td>
              </tr>
            ) : (
              keyedRows.map(({ key, row }) => (
                <tr
                  key={key}
                  className="hover:bg-muted/40"
                  data-testid={`${testId}-row-${key}`}
                >
                  {selectable ? (
                    <td className="w-10 px-3 py-2">
                      <Checkbox
                        checked={selectedKeys.has(key)}
                        onChange={() => toggleOne(key)}
                        aria-label={t('dataTable.select_row')}
                        data-testid={`${testId}-select-${key}`}
                      />
                    </td>
                  ) : null}
                  {columns.map((col) => (
                    <td
                      key={col.id}
                      className={cn(
                        'px-4 py-2 text-foreground',
                        col.align === 'end' && 'text-end',
                        col.hideOnMobile && 'hidden sm:table-cell',
                      )}
                    >
                      {col.cell(row)}
                    </td>
                  ))}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
      {pagination ? <Pagination pagination={pagination} /> : null}
    </div>
  );
}
